<template>
  <div>
    <label v-if="caption" :for="name" class="block text-xs font-bold relative text-gray-700">
      {{ caption }} <span v-if="required === true" class="absolute -top-0.5 ml-1 text-red-500"> * </span>
    </label>
    <div class="flex relative wms-stack" :class="caption ? 'mt-1' : ''">
      <!--
        ドロップダウン展開中、vue-multiselectは自身にz-index:50を付与する
        （.multiselect--active）。InputIconは固定でz-index:1のため、
        展開時にアイコンがMultiselectの裏に隠れてしまう。
        wms-icon-zクラスでそれより高いz-indexを与えて常に手前に出す。
        ＊wms-stack（isolation:isolate）でこの欄の中だけの独立した重なり順にし、
        z-indexの比較が他のWmsSearchSelect（別フィールド）に影響しないようにする。
      -->
      <InputIcon v-if="error" class="wms-icon-z" iconName="ExclamationCircle" iconColor="red" :message="errorMessage || '入力してください'" />
      <Multiselect
        :id="name"
        :value="value"
        :options="options"
        :track-by="trackBy"
        :label="label"
        :custom-label="optionLabel"
        :multiple="false"
        :searchable="true"
        :internal-search="true"
        :allow-empty="true"
        :close-on-select="true"
        :clear-on-select="true"
        :preserve-search="false"
        :show-labels="false"
        :disabled="disabled"
        :placeholder="placeholder"
        :options-limit="1000"
        class="w-full wms-search-select"
        :class="[error ? 'error' : '', clearable && !disabled && value ? 'clearable' : '']"
        @input="onInput"
      >
        <!-- 選択後の表示 -->
        <template slot="singleLabel" slot-scope="{ option }">{{ dispSelectedLabel(option) }}</template>
        <span slot="noResult">該当するデータがありません</span>
        <span slot="noOptions">選択肢がありません</span>
      </Multiselect>
      <!-- クリアボタン（右端の標準ドロップダウン矢印(.multiselect__select、幅40px)と重ならない位置に置く） -->
      <div
        v-if="clearable && !disabled && value"
        class="absolute inset-y-0 right-12 flex items-center"
        style="z-index: 1;"
        @mousedown.prevent.stop
      >
        <Icon
          iconName="X"
          :clickable="true"
          class="w-4 h-4 text-gray-400"
          @click="onInput(null)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import InputIcon from '@components/InputIcon.vue'
import Icon from '@components/Icon.vue'

/**
 * 検索付き単一選択
 * 入力すると候補を絞り込めるが、候補から選んだものしか確定しない
 * 選択したオブジェクト（クリア時はnull）をinputイベントで返す
 */
export default {
  components: {
    Multiselect,
    InputIcon,
    Icon
  },

  props: {
    name: {
      type: String,
      default: ''
    },
    caption: {
      type: String,
      default: ''
    },
    value: {
      type: Object,
      default: null
    },
    options: {
      type: Array,
      default: () => []
    },
    // 一意キー
    trackBy: {
      type: String,
      required: true
    },
    // 表示・検索に使う項目名
    label: {
      type: String,
      default: ''
    },
    // 表示・検索に使う文字列を返す関数（labelより優先）
    customLabel: {
      type: Function,
      default: null
    },
    // 選択後に表示する文字列を返す関数（未指定なら候補と同じ）
    selectedLabel: {
      type: Function,
      default: null
    },
    placeholder: {
      type: String,
      default: '入力して検索'
    },
    required: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    }
  },

  methods: {
    /**
     * 候補のラベル（検索対象）
     */
    optionLabel(option) {
      if (!option) {
        return ''
      }
      if (this.customLabel) {
        return this.customLabel(option) || ''
      }
      return option[this.label] || ''
    },

    /**
     * 選択後のラベル
     */
    dispSelectedLabel(option) {
      if (this.selectedLabel) {
        return this.selectedLabel(option) || ''
      }
      return this.optionLabel(option)
    },

    /**
     * 選択／クリア
     */
    onInput(option) {
      this.$emit('input', option || null)
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
  /*
   * 既存の入力欄（サブタスク名＝WmsTextInput）にDevToolsの実測値で高さを合わせる。
   * 実測: content 20px（text-sm, line-height 1.25rem）+ padding 6px×2 + border 1px×2 = 34px
   * （「38px」としていたのは誤り。text-baseのline-height 1.5remで計算していたが、
   * 実際はtext-smのline-height 1.25remだった）
   * min-height+paddingの積み上げ方式だと内部の行間・marginが加算されて
   * 想定より高くなってしまうため、高さを固定してFlexboxで縦中央寄せする。
   * ルート要素（.multiselect）自体にもvue-multiselect既定でmin-height:40pxが
   * かかっているため、内側の.multiselect__tagsだけでなくこちらも上書きする。
   */
  .wms-search-select {
    min-height: 34px !important;
  }

  .wms-search-select >>> .multiselect__tags {
    display: flex;
    align-items: center;
    box-sizing: border-box;
    height: 34px;
    min-height: 34px;
    /* 枠線・角丸は他の入力欄（border-gray-300 rounded）に合わせる */
    border-radius: 0.25rem;
    border-color: rgb(209, 213, 219);
    padding: 0 40px 0 0.75rem;
    font-size: 0.875rem;
    overflow: hidden;
  }

  /* フォーカス（ドロップダウン展開）時の枠線も他の入力欄と同じindigo-500にする */
  .wms-search-select >>> .multiselect--active .multiselect__tags {
    border-color: #6366f1;
  }

  .wms-search-select.clearable >>> .multiselect__tags {
    /* 右端のドロップダウン矢印（40px）＋クリアボタンの両方を避けて折り返す */
    padding-right: 76px;
  }

  .wms-search-select.error >>> .multiselect__tags {
    padding-left: 32px;
  }

  .wms-search-select >>> .multiselect__single,
  .wms-search-select >>> .multiselect__input,
  .wms-search-select >>> .multiselect__placeholder {
    box-sizing: border-box;
    font-size: 0.875rem;
    line-height: 1.25rem;
    padding: 0;
    margin: 0;
    min-height: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* 狭い列でも候補が読めるようにする */
  .wms-search-select >>> .multiselect__content-wrapper {
    min-width: 14rem;
    z-index: 50;
  }

  .wms-search-select >>> .multiselect__option {
    font-size: 0.875rem;
    white-space: normal;
  }

  /*
   * 選択済みの項目は、ホバーしていなくても薄いindigo色を常時つけておく
   * （カーソル位置＝先頭の項目と紛らわしいとの指摘のため）。
   * ホバー時（--highlight）は下の濃いindigoが優先される（セレクタの詳細度が高いため）。
   */
  .wms-search-select >>> .multiselect__option--selected {
    background: #eef2ff;
    color: #4338ca;
  }

  /* 候補の選択色を既存のWmsSelect（indigo-600 / 白文字）に合わせる（既定は緑・赤） */
  .wms-search-select >>> .multiselect__option--highlight,
  .wms-search-select >>> .multiselect__option--highlight:after,
  .wms-search-select >>> .multiselect__option--selected.multiselect__option--highlight,
  .wms-search-select >>> .multiselect__option--selected.multiselect__option--highlight:after {
    background: #4f46e5;
    color: #fff;
  }

  /*
   * ドロップダウン展開中（.multiselect--active）はvue-multiselectがz-index:50を
   * 自身に付与するため、エラーアイコン（既定z-index:1のインラインスタイル）が
   * 裏に隠れてしまう。常に手前に出るよう、それより高いz-indexを!importantで強制する。
   */
  .wms-icon-z {
    z-index: 51 !important;
  }

  /*
   * 上記のz-index:51は、isolation:isolateで重なり順の比較範囲をこの欄の中だけに
   * 限定しないと、ページ内の別のWmsSearchSelect（例:下にある担当者欄）の
   * アイコンと全体で比較されてしまい、他欄のアイコンがこのドロップダウンの
   * 上に浮き出て見えてしまう。
   */
  .wms-stack {
    isolation: isolate;
  }

  /*
   * isolationだけだと、この欄のドロップダウンが「ページの他の内容より手前に出る」
   * という本来vue-multiselectのz-index:50が担っていた効果まで失われ、
   * 後に続く別の欄（依頼主・依頼日など）がドロップダウンの上に
   * 覆いかぶさって表示されてしまう。
   * 自分の中のMultiselectが開いている間（.multiselect--active）だけ、
   * 欄全体をページの最前面に引き上げて元の見え方に戻す。
   */
  .wms-stack:has(.multiselect--active) {
    z-index: 50;
  }
</style>
